import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Grid, Box } from "@mui/material";
import {
  deleteRestaurant,
  getRestaurantsError,
  getRestaurantsStatus,
  selectAllRestaurants,
  getRestaurantsSuccess,
  updateStatus,
  fetchRestaurants,
} from "../../features/restaurantsSlice";
import Loading from "../../components/loading";
import Error from "../../components/Error";
import RestaurantCard from "../../components/restaurantCard";
import NoData from "../../components/noData";
import AppBarSearch from "../../global/appBarSearch";
import AlertDialog from "../../components/alertDialog";
import { toast } from "react-toastify";
import RestaurantTopBar from "../../components/restaurantTopBar";
import { useRestaurant } from "../../hooks/RestaurantContext";
import { useAuth } from "../../hooks/authHook";
import { UserRole } from "../../models/user.model";
import MenuItemFilter from "../../components/filters/menuItemFilter";
import { CouponStatusList } from "../../models/coupon.model";
import { ResponsiveProvider } from "../../hooks/uiHook";
import { useTranslation } from "react-i18next";

const Restaurant = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { role, assignedRestaurants } = useAuth();
  const [search, setSearch] = useState("");
  const [restaurantStatusFilter, setRestaurantStatusFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [cardId, setCardId] = useState(null);
  const [localRestaurants, setLocalRestaurants] = useState([]);
  const { setRestaurantId, setRestaurantName, setRestaurantLogo, setCurrency } =
    useRestaurant();
  const status = useSelector(getRestaurantsStatus);
  const error = useSelector(getRestaurantsError);
  const restaurants = useSelector(selectAllRestaurants);
  const success = useSelector(getRestaurantsSuccess);
  const canManage = role === UserRole.admin;

  const handleClickOpen = (id) => {
    setOpen(true);
    setCardId(id);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    navigate("add");
  };

  const handleModify = (data) => {
    navigate("modify", {
      state: {
        restaurant: data,
      },
    });
  };

  useEffect(() => {
    const alreadyLoaded = restaurants.length > 0 && status === "fetchData";
    if (!alreadyLoaded) {
      dispatch(fetchRestaurants());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (status === "fetchData") setLocalRestaurants(restaurants);
  }, [restaurants, status]);

  const filteredRestaurantsMemo = useMemo(() => {
    let filtered = localRestaurants || [];

    if (role === UserRole.manager) {
      return filtered?.filter((restaurant) =>
        assignedRestaurants.some((assigned) => assigned.id === restaurant._id)
      );
    }

    const query = search.trim().toLowerCase();
    if (query) {
      filtered = filtered.filter(
        (restaurant) =>
          (restaurant.name || "").toLowerCase().includes(query) ||
          (restaurant.description || "").toLowerCase().includes(query) ||
          (restaurant.address || "").toLowerCase().includes(query)
      );
    }

    if (restaurantStatusFilter !== "") {
      filtered = filtered.filter(
        (restaurant) =>
          String(restaurant.active) === String(restaurantStatusFilter)
      );
    }

    return filtered;
  }, [
    localRestaurants,
    role,
    search,
    restaurantStatusFilter,
    assignedRestaurants,
  ]);

  useEffect(() => {
    if (status !== "fetchData") return;

    let restaurant = null;
    if (role === UserRole.manager && assignedRestaurants.length === 1) {
      restaurant = restaurants.find((r) => r._id === assignedRestaurants[0].id);
    } else if (role === UserRole.admin && restaurants.length === 1) {
      restaurant = restaurants[0];
    }

    if (restaurant) {
      setRestaurantId(restaurant._id);
      setRestaurantName(restaurant.name);
      setRestaurantLogo(restaurant.logo);
      setCurrency(restaurant.currency);
      navigate("/dashboard");
    }
  }, [
    status,
    role,
    assignedRestaurants,
    restaurants,
    setRestaurantId,
    setRestaurantName,
    setRestaurantLogo,
    setCurrency,
    navigate,
  ]);

  const handleViewRestaurant = (restaurant) => {
    setRestaurantId(restaurant._id);
    setRestaurantName(restaurant.name);
    setRestaurantLogo(restaurant.logo);
    setCurrency(restaurant.currency || "€");
    navigate("/dashboard");
  };

  useEffect(() => {
    if (status === "deleteSuccess") {
      toast.success(success);
      setLocalRestaurants((prev) => prev.filter((item) => item._id !== cardId));
      dispatch(updateStatus());
    } else if (status === "deleteError") {
      toast.error(error);
    }
  }, [cardId, dispatch, error, status, success]);

  return (
    <div className="main-application">
      <RestaurantTopBar />
      <ResponsiveProvider maxWidth={900}>
        <AppBarSearch
          searchText={t("search.byNameOrDescription")}
          handleSubmit={canManage ? handleSubmit : null}
          handleSearch={(e) => setSearch(e.target.value)}
          title={t("pages.myRestaurants")}
          buttonTitle={t("actions.addRestaurant")}
          children={
            <MenuItemFilter
              label={t("filters.allStates")}
              value={restaurantStatusFilter}
              onChange={(e) => setRestaurantStatusFilter(e.target.value)}
              filters={CouponStatusList}
            />
          }
        />
      </ResponsiveProvider>
      <main>
        <Box sx={{ my: 2 }}>
          <Grid container spacing={2.5}>
            {status === "loadingFetchData" ? (
              <Loading />
            ) : status === "fetchError" ? (
              <Error>{error}</Error>
            ) : (
              <>
                {filteredRestaurantsMemo && filteredRestaurantsMemo.length > 0 ? (
                  filteredRestaurantsMemo.map((card) => (
                    <RestaurantCard
                      key={card._id}
                      data={card}
                      search={search}
                      canManage={canManage}
                      onOpen={() => handleViewRestaurant(card)}
                      onEdit={() => handleModify(card)}
                      onDelete={() => handleClickOpen(card._id)}
                    />
                  ))
                ) : (
                  <NoData />
                )}
                <AlertDialog
                  handleClose={() => setOpen(false)}
                  open={open}
                  name={"restaurant"}
                  cardId={cardId}
                  deleteData={() => dispatch(deleteRestaurant(cardId))}
                />
              </>
            )}
          </Grid>
        </Box>
      </main>
    </div>
  );
};

export default Restaurant;
